import React from "react"
import ServiceFaq from "./ServiceFaq";

// ── Inline hero illustration ──────────────────────────────────────────────────

const HeroIllustration: React.FC = () => (
    <div style={{
        background: "linear-gradient(135deg, #0f2040 0%, #1a3460 100%)",
        borderRadius: 16,
        padding: "48px 40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 40,
        overflow: "hidden",
        position: "relative",
    }}>
        {/* Background grid */}
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.06 }} xmlns="http://www.w3.org/2000/svg">
            <defs>
                <pattern id="sdgrid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M40 0H0V40" fill="none" stroke="white" strokeWidth="0.8"/>
                </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#sdgrid)"/>
        </svg>

        {/* Left: API code block */}
        <div style={{ flex: 1, position: "relative", zIndex: 1 }}>
            <div style={{ background: "rgba(0,0,0,0.4)", borderRadius: 10, padding: "20px 24px", fontFamily: "monospace", fontSize: 13, lineHeight: 1.7 }}>
                <div style={{ color: "rgba(255,255,255,0.4)", marginBottom: 8, fontSize: 11 }}>POST /v1/ekyc/verify</div>
                <div><span style={{ color: "#00c896" }}>status</span><span style={{ color: "white" }}>: </span><span style={{ color: "#f59e0b" }}>"verified"</span></div>
                <div><span style={{ color: "#00c896" }}>face_match</span><span style={{ color: "white" }}>: </span><span style={{ color: "#3b82f6" }}>98.4</span></div>
                <div><span style={{ color: "#00c896" }}>liveness</span><span style={{ color: "white" }}>: </span><span style={{ color: "#3b82f6" }}>true</span></div>
                <div><span style={{ color: "#00c896" }}>doc_valid</span><span style={{ color: "white" }}>: </span><span style={{ color: "#3b82f6" }}>true</span></div>
                <div><span style={{ color: "#00c896" }}>aml_clear</span><span style={{ color: "white" }}>: </span><span style={{ color: "#3b82f6" }}>true</span></div>
                <div><span style={{ color: "#00c896" }}>latency_ms</span><span style={{ color: "white" }}>: </span><span style={{ color: "#3b82f6" }}>487</span></div>
            </div>
        </div>

        {/* Center: Arrow */}
        <div style={{ color: "#00c896", fontSize: 32, position: "relative", zIndex: 1 }}>→</div>

        {/* Right: Result card */}
        <div style={{ flex: 1, position: "relative", zIndex: 1 }}>
            <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 10, border: "1px solid rgba(0,200,150,0.25)", padding: "20px 24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(0,200,150,0.15)", border: "1.5px solid #00c896", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="#00c896" strokeWidth="1.6"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#00c896" strokeWidth="1.6" strokeLinecap="round"/></svg>
                    </div>
                    <div>
                        <div style={{ color: "white", fontWeight: 700, fontSize: 14 }}>Rian Hidayat</div>
                        <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 12 }}>NIK ·· 3271xxxx</div>
                    </div>
                    <div style={{ marginLeft: "auto", background: "rgba(0,200,150,0.15)", border: "1px solid #00c896", borderRadius: 20, padding: "3px 10px", color: "#00c896", fontSize: 11, fontWeight: 700 }}>✓ VERIFIED</div>
                </div>
                {[
                    { label: "Face Match", val: "98.4%", color: "#00c896" },
                    { label: "Liveness", val: "PASS", color: "#00c896" },
                    { label: "AML Screen", val: "CLEAR", color: "#3b82f6" },
                ].map((r) => (
                    <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", fontSize: 12 }}>
                        <span style={{ color: "rgba(255,255,255,0.55)" }}>{r.label}</span>
                        <span style={{ color: r.color, fontWeight: 700 }}>{r.val}</span>
                    </div>
                ))}
                <div style={{ marginTop: 10, fontSize: 11, color: "rgba(255,255,255,0.35)", textAlign: "right" }}>⚡ 487ms · via /v1/ekyc/verify</div>
            </div>
        </div>
    </div>
);

// ── Service cards ─────────────────────────────────────────────────────────────

interface ServiceCardProps {
    icon: React.ReactNode;
    title: string;
    desc: string;
    tags: string[];
}

const ServiceCard: React.FC<ServiceCardProps> = ({ icon, title, desc, tags }) => (
    <div style={{
        background: "#ffffff",
        border: "1px solid rgba(15,32,64,0.08)",
        borderRadius: 12,
        padding: "28px 24px",
        boxShadow: "0 4px 20px rgba(15,32,64,0.06)",
        height: "100%",
    }}>
        <div style={{ marginBottom: 16 }}>{icon}</div>
        <h4 style={{ color: "#0f2040", fontWeight: 700, fontSize: 17, marginBottom: 10 }}>{title}</h4>
        <p style={{ color: "rgba(15,32,64,0.6)", fontSize: 14, lineHeight: 1.65, marginBottom: 16 }}>{desc}</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {tags.map((t) => (
                <span key={t} style={{ background: "rgba(0,200,150,0.08)", border: "1px solid rgba(0,200,150,0.25)", color: "#00a07a", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>{t}</span>
            ))}
        </div>
    </div>
);

const IconFace = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="14" r="7" stroke="#00c896" strokeWidth="1.8"/>
        <circle cx="17.5" cy="12.5" r="1.5" fill="#00c896"/>
        <circle cx="22.5" cy="12.5" r="1.5" fill="#00c896"/>
        <path d="M17 17c0 0 1 2 3 2s3-2 3-2" stroke="#00c896" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M8 34c0-6.6 5.4-12 12-12s12 5.4 12 12" stroke="#00c896" strokeWidth="1.8" strokeLinecap="round"/>
        <rect x="4" y="4" width="8" height="2" rx="1" fill="#00c896" opacity="0.5"/>
        <rect x="4" y="4" width="2" height="8" rx="1" fill="#00c896" opacity="0.5"/>
        <rect x="28" y="4" width="8" height="2" rx="1" fill="#00c896" opacity="0.5"/>
        <rect x="34" y="4" width="2" height="8" rx="1" fill="#00c896" opacity="0.5"/>
    </svg>
);

const IconDoc = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <rect x="8" y="6" width="24" height="28" rx="4" stroke="#00c896" strokeWidth="1.8" fill="rgba(0,200,150,0.06)"/>
        <path d="M14 16h12M14 21h12M14 26h8" stroke="#00c896" strokeWidth="1.6" strokeLinecap="round"/>
        <rect x="13" y="10" width="8" height="4" rx="1.5" fill="rgba(0,200,150,0.25)" stroke="#00c896" strokeWidth="1"/>
    </svg>
);

const IconShield = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <path d="M20 5L8 10v10c0 8.8 5.1 17 12 19.2C27 37 32 28.8 32 20V10L20 5z" stroke="#00c896" strokeWidth="1.8" strokeLinejoin="round" fill="rgba(0,200,150,0.06)"/>
        <path d="M14 20l4 4 8-8" stroke="#00c896" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const IconAPI = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <rect x="6" y="10" width="28" height="20" rx="4" stroke="#00c896" strokeWidth="1.8" fill="rgba(0,200,150,0.06)"/>
        <path d="M13 20l-4 3 4 3" stroke="#00c896" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M27 20l4 3-4 3" stroke="#00c896" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M22 16l-4 8" stroke="#00c896" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
);

const IconRisk = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="14" stroke="#00c896" strokeWidth="1.8" fill="rgba(0,200,150,0.06)"/>
        <path d="M20 12v10" stroke="#00c896" strokeWidth="2.2" strokeLinecap="round"/>
        <circle cx="20" cy="26" r="1.5" fill="#00c896"/>
    </svg>
);

const IconData = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <ellipse cx="20" cy="12" rx="12" ry="5" stroke="#00c896" strokeWidth="1.8" fill="rgba(0,200,150,0.06)"/>
        <path d="M8 12v8c0 2.8 5.4 5 12 5s12-2.2 12-5v-8" stroke="#00c896" strokeWidth="1.8"/>
        <path d="M8 20v8c0 2.8 5.4 5 12 5s12-2.2 12-5v-8" stroke="#00c896" strokeWidth="1.8"/>
    </svg>
);

const services: ServiceCardProps[] = [
    {
        icon: <IconFace />,
        title: "eKYC & Face Verification",
        desc: "Real-time identity verification with biometric face matching, anti-spoofing liveness detection, and document photo comparison. 99.8% accuracy, <500ms response.",
        tags: ["Face Match", "Liveness Detection", "Anti-Spoofing"],
    },
    {
        icon: <IconDoc />,
        title: "Document OCR & Extraction",
        desc: "Automatically extract data from KTP, SIM, Passport, and NPWP using AI OCR. Includes document authenticity validation and forgery detection.",
        tags: ["KTP", "Passport", "SIM", "NPWP"],
    },
    {
        icon: <IconShield />,
        title: "AML & Watchlist Screening",
        desc: "Screen identities against PPATK, international sanctions (UN, OFAC, EU), and PEP lists in real-time. Fully compliant with BI and OJK regulations.",
        tags: ["PPATK", "OFAC", "PEP Screening", "OJK"],
    },
    {
        icon: <IconRisk />,
        title: "Risk Scoring & Fraud Detection",
        desc: "Our ML models generate risk scores based on behavioral patterns, device fingerprints, and transaction history — stopping fraud before it happens.",
        tags: ["Fraud Prevention", "ML Scoring", "Device Intelligence"],
    },
    {
        icon: <IconAPI />,
        title: "REST API & SDK",
        desc: "Integrate in minutes with a well-documented REST API. SDKs available for Python, Node.js, and Go. Full sandbox environment for testing.",
        tags: ["REST API", "Python SDK", "Node.js SDK", "Sandbox"],
    },
    {
        icon: <IconData />,
        title: "Data Enrichment",
        desc: "Enrich customer profiles with data from multiple sources — Dukcapil, BI Checking, and third-party databases — in a single API call.",
        tags: ["Dukcapil", "BI Checking", "Multi-source"],
    },
];

// ── Main Component ────────────────────────────────────────────────────────────

const ServiceDetails: React.FC = () => {
    const outcomes = [
        "10x faster customer onboarding with zero manual review.",
        "Up to 95% reduction in fraud with liveness detection.",
        "OJK, BI SNAP, and PPATK compliant out of the box.",
        "API integration in under 1 business day with full documentation.",
        "99.9% uptime SLA with 24/7 enterprise support.",
        "Complete audit trail for regulatory reporting requirements.",
    ];

    return (
        <section className="service-details pos-rel">
            <div className="container">

                {/* ── Hero Illustration ── */}
                <div className="mb-50">
                    <HeroIllustration />
                </div>

                {/* ── Overview ── */}
                <h2 className="details-content-title mb-15">
                    Identity Verification Platform for Regulated Industries
                </h2>
                <p>
                    TrustEngine is an AI-powered identity verification platform built for fintech, banking, healthcare, and government institutions in Indonesia. We provide eKYC, AML screening, and fraud detection through a single integrated API — enabling businesses to verify identities in seconds, not days.
                </p>
                <p className="mt-30">
                    Built on scalable cloud-native infrastructure, TrustEngine handles anywhere from 1,000 to 5 million verifications per month without any code changes. All data is processed in compliance with ISO 27001, SOC 2 Type II, and Indonesia's PDPA data protection regulations.
                </p>

                {/* ── Service Process ── */}
                <ServiceFaq />

                {/* ── Our Services ── */}
                <div className="services-outcome-wrap">
                    <h2 className="details-content-title mb-15">Our Services</h2>
                    <p>
                        One platform, all your identity verification needs. From eKYC to AML screening, TrustEngine provides a complete suite for secure and compliant onboarding.
                    </p>

                    <div className="row mt-35">
                        {services.map((s) => (
                            <div className="col-lg-4 col-md-6 mt-30" key={s.title}>
                                <ServiceCard {...s} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Outcomes ── */}
                <div className="services-outcome-wrap mt-75">
                    <h2 className="details-content-title mb-15">What You Get</h2>
                    <p>
                        Hundreds of companies across Indonesia use TrustEngine to accelerate onboarding, reduce fraud, and automate regulatory compliance.
                    </p>

                    <ul className="service-outcome-list list-unstyled mt-35">
                        {outcomes.map((item) => (
                            <li key={item}>
                                <span>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path opacity="0.2" d="M24 12C24 13.024 22.742 13.868 22.49 14.812C22.23 15.788 22.888 17.148 22.394 18.002C21.892 18.87 20.382 18.974 19.678 19.678C18.974 20.382 18.87 21.892 18.002 22.394C17.148 22.888 15.788 22.23 14.812 22.49C13.868 22.742 13.024 24 12 24C10.976 24 10.132 22.742 9.188 22.49C8.212 22.23 6.852 22.888 5.998 22.394C5.13 21.892 5.026 20.382 4.322 19.678C3.618 18.974 2.108 18.87 1.606 18.002C1.112 17.148 1.77 15.788 1.51 14.812C1.258 13.868 0 13.024 0 12C0 10.976 1.258 10.132 1.51 9.188C1.77 8.212 1.112 6.852 1.606 5.998C2.108 5.13 3.618 5.026 4.322 4.322C5.026 3.618 5.13 2.108 5.998 1.606C6.852 1.112 8.212 1.77 9.188 1.51C10.132 1.258 10.976 0 12 0C13.024 0 13.868 1.258 14.812 1.51C15.788 1.77 17.148 1.112 18.002 1.606C18.87 2.108 18.974 3.618 19.678 4.322C20.382 5.026 21.892 5.13 22.394 5.998C22.888 6.852 22.23 8.212 22.49 9.188C22.742 10.132 24 10.976 24 12Z" fill="#00FF97"/>
                                        <path d="M15.5559 9.14076L11.3992 13.178L9.24437 11.0869C8.77664 10.6326 8.01773 10.6326 7.55001 11.0869C7.08229 11.5412 7.08229 12.2783 7.55001 12.7326L10.5729 15.6686C11.0279 16.1105 11.7668 16.1105 12.2218 15.6686L17.2484 10.7864C17.7162 10.3321 17.7162 9.59504 17.2484 9.14076C16.7807 8.68648 16.0236 8.68648 15.5559 9.14076Z" fill="#00FF97"/>
                                    </svg>
                                </span>
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* ── Bottom padding ── */}
                <div style={{ height: 80 }} />
            </div>
        </section>
    );
};

export default ServiceDetails;
